import type { GenerateStudydoImageResult } from "./imageGenerationService";
import type { PollinationsImageAttempt } from "./pollinationsImage";

/** Canonical image API response kinds */
export type ImageApiKind = "image" | "no_image" | "error";

/** SUCCESS: image found and returned */
export interface ImageApiSuccess {
  ok: true;
  kind: "image";
  imageUrl: string;
  provider: string;
  source: "free" | "paid" | "cache";
  providerAssetId: string | null;
  model: string | null;
  query: string | null;
  imageBase64?: string;
  contentType?: string;
  cacheHit?: boolean;
  attempts?: PollinationsImageAttempt[];
}

/** NO_IMAGE: controlled result, no image found (not an error) */
export interface ImageApiNoImage {
  ok: true;
  kind: "no_image";
  reason: "no_match" | "low_relevance" | "provider_exhausted";
  provider: string | null;
  source: "free" | "paid" | "cache" | null;
  imageUrl: null;
  attempts?: PollinationsImageAttempt[];
}

/** ERROR: actual failure */
export interface ImageApiError {
  ok: false;
  kind: "error";
  errorCode: string;
  message: string;
  provider?: string;
  status?: number;
  attempts?: PollinationsImageAttempt[];
}

export type CanonicalImageApiResponse = ImageApiSuccess | ImageApiNoImage | ImageApiError;

export interface NormalizedImageResult {
  ok: boolean;
  kind?: ImageApiKind;
  status: number;
  imageBase64?: string;
  imageUrl?: string;
  contentType?: string;
  provider?: string;
  providerAssetId?: string;
  model?: string;
  source?: string;
  cacheHit?: boolean;
  errorCode?: string;
  errorMessage?: string;
  attempts?: PollinationsImageAttempt[];
  /** For client: no_image is controlled, not an exception */
  noImageControlled?: boolean;
}

function sanitizeOptionalString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function sanitizeStatus(value: unknown, fallbackStatus: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallbackStatus;
}

function mapSourceToCanonical(
  source?: string
): "free" | "paid" | "cache" {
  const s = (source ?? "").toLowerCase();
  if (s === "cache") return "cache";
  if (["pixabay", "pexels", "openverse", "wikimedia", "pollinations", "workers-ai"].includes(s)) {
    return s === "pollinations" || s === "workers-ai" ? "paid" : "free";
  }
  return "free";
}

/**
 * Single normalizer for ALL backend branches. Every path (free, paid, cache, no-image, error)
 * must go through this. No raw provider payload goes to frontend.
 */
export function toCanonicalImageResponse(
  result: GenerateStudydoImageResult,
  toBase64: (buffer: ArrayBuffer) => string
): CanonicalImageApiResponse {
  if (result.ok) {
    const provider = result.provider ?? "unknown";
    const source = mapSourceToCanonical(result.source ?? result.provider);
    const imageUrl = sanitizeOptionalString(result.imageUrl);
    if (imageUrl) {
      console.info("[generate-image] image_api_success", {
        provider,
        source,
        cacheHit: result.cacheHit ?? false,
      });
      return {
        ok: true,
        kind: "image",
        imageUrl,
        provider,
        source,
        providerAssetId: result.providerAssetId ?? null,
        model: result.model ?? null,
        query: null,
        cacheHit: result.cacheHit,
        attempts: result.attempts,
      };
    }
    const hasImageBytes = Boolean(result.imageBytes && result.imageBytes.byteLength > 0);
    const contentType = sanitizeOptionalString(result.contentType);
    if (hasImageBytes && contentType) {
      console.info("[generate-image] image_api_success", {
        provider,
        source,
        cacheHit: result.cacheHit ?? false,
      });
      return {
        ok: true,
        kind: "image",
        imageUrl: "", // Will be sent as imageBase64
        provider,
        source,
        providerAssetId: result.providerAssetId ?? null,
        model: result.model ?? null,
        query: null,
        imageBase64: toBase64(result.imageBytes as ArrayBuffer),
        contentType,
        cacheHit: result.cacheHit,
        attempts: result.attempts,
      };
    }
    console.error("[generate-image] image_payload_invalid", {
      provider: result.provider,
      hasImageUrl: Boolean(result.imageUrl),
      hasImageBytes,
    });
    return {
      ok: false,
      kind: "error",
      errorCode: "INVALID_RESPONSE",
      message: "Image generation returned no asset",
      provider,
      status: 500,
      attempts: result.attempts,
    };
  }

  const provider = result.provider ?? "unknown";
  const status =
    typeof result.status === "number" && result.status >= 200 && result.status < 600
      ? result.status
      : 500;

  if (status === 200) {
    console.info("[generate-image] image_api_no_image", {
      provider,
      errorCode: result.errorCode,
    });
    return {
      ok: true,
      kind: "no_image",
      reason: "provider_exhausted",
      provider: provider === "unknown" ? null : provider,
      source: null,
      imageUrl: null,
      attempts: result.attempts,
    };
  }

  console.info("[generate-image] image_api_error", {
    provider,
    status,
    errorCode: result.errorCode,
  });
  return {
    ok: false,
    kind: "error",
    errorCode: result.errorCode ?? "UNKNOWN",
    message: result.error ?? "Image generation failed",
    provider,
    status,
    attempts: result.attempts,
  };
}

export function hasUsableImagePayload(payload: Pick<NormalizedImageResult, "imageUrl" | "imageBase64">): boolean {
  return Boolean(sanitizeOptionalString(payload.imageUrl) || sanitizeOptionalString(payload.imageBase64));
}

/**
 * @deprecated Use toCanonicalImageResponse. Converts canonical response to NormalizedImageResult.
 */
export function buildNormalizedImageApiResult(
  result: GenerateStudydoImageResult,
  toBase64: (buffer: ArrayBuffer) => string
): NormalizedImageResult {
  const canonical = toCanonicalImageResponse(result, toBase64);
  if (canonical.kind === "image") {
    return {
      ok: true,
      status: 200,
      imageUrl: canonical.imageUrl || undefined,
      imageBase64: canonical.imageBase64,
      contentType: canonical.contentType,
      provider: canonical.provider,
      providerAssetId: canonical.providerAssetId ?? undefined,
      model: canonical.model ?? undefined,
      source: canonical.source,
      cacheHit: canonical.cacheHit,
      attempts: canonical.attempts,
    };
  }
  if (canonical.kind === "no_image") {
    return {
      ok: true,
      kind: "no_image",
      status: 200,
      noImageControlled: true,
      provider: canonical.provider ?? undefined,
      errorCode: undefined,
      errorMessage: "No image found",
      attempts: canonical.attempts,
    };
  }
  return {
    ok: false,
    kind: "error",
    status: canonical.status ?? 500,
    provider: canonical.provider,
    errorCode: canonical.errorCode,
    errorMessage: canonical.message,
    attempts: canonical.attempts,
  };
}

/**
 * Normalizes API response for client. Understands canonical contract:
 * - kind === 'image' → success, return image
 * - kind === 'no_image' → controlled no-image, NOT an exception
 * - kind === 'error' or malformed → error, caller should throw
 * Backward compat: infers kind from ok/status when kind is absent (old API format).
 */
export function normalizeImageApiPayload(
  payload: unknown,
  responseStatus: number,
  responseOk: boolean
): NormalizedImageResult {
  const record = typeof payload === "object" && payload !== null ? (payload as Record<string, unknown>) : {};
  const kind = sanitizeOptionalString(record.kind) as ImageApiKind | undefined;
  const payloadOk = record.ok === true;
  const status = sanitizeStatus(record.status, responseStatus);
  const imageUrl = sanitizeOptionalString(record.imageUrl);
  const imageBase64 = sanitizeOptionalString(record.imageBase64);
  const provider = sanitizeOptionalString(record.provider) ?? "unknown";
  const model = sanitizeOptionalString(record.model) ?? provider;
  const source = sanitizeOptionalString(record.source) ?? provider;

  const normalized: NormalizedImageResult = {
    ok: payloadOk,
    kind,
    status,
    imageUrl,
    imageBase64,
    contentType: sanitizeOptionalString(record.contentType),
    provider,
    providerAssetId: sanitizeOptionalString(record.providerAssetId),
    model,
    source,
    cacheHit: typeof record.cacheHit === "boolean" ? record.cacheHit : undefined,
    errorCode: sanitizeOptionalString(record.errorCode),
    errorMessage: sanitizeOptionalString(record.error) || sanitizeOptionalString(record.message) || sanitizeOptionalString(record.errorMessage),
    attempts: Array.isArray(record.attempts) ? (record.attempts as PollinationsImageAttempt[]) : undefined,
  };

  if (kind === "image" || (payloadOk && hasUsableImagePayload(normalized))) {
    console.info("[image-api] image_api_success", { provider, hasImageUrl: Boolean(imageUrl), hasBase64: Boolean(imageBase64) });
    return { ...normalized, ok: true, kind: "image" };
  }

  if (kind === "no_image" || (payloadOk && status === 200 && !hasUsableImagePayload(normalized) && responseOk)) {
    console.info("[image-api] image_api_no_image", { provider, errorCode: record.errorCode });
    return {
      ...normalized,
      ok: true,
      kind: "no_image",
      noImageControlled: true,
      imageUrl: undefined,
      imageBase64: undefined,
    };
  }

  if (kind === "error" || (!payloadOk && !responseOk) || (kind === undefined && !payloadOk && status >= 400)) {
    console.info("[image-api] image_api_error", { provider, status, errorCode: record.errorCode });
    return {
      ...normalized,
      ok: false,
      kind: "error",
      status: status >= 400 ? status : responseStatus >= 400 ? responseStatus : 500,
      errorCode: normalized.errorCode ?? "UNKNOWN",
      errorMessage: normalized.errorMessage ?? "Image generation failed",
    };
  }

  if (!payloadOk && status === 200 && responseOk) {
    console.info("[image-api] image_api_no_image (legacy)", { provider, errorCode: record.errorCode });
    return {
      ...normalized,
      ok: true,
      kind: "no_image",
      noImageControlled: true,
      status: 200,
      imageUrl: undefined,
      imageBase64: undefined,
      errorCode: undefined,
    };
  }

  if (payloadOk && !hasUsableImagePayload(normalized)) {
    console.error("[image-api] image_payload_invalid", {
      status: responseStatus,
      provider,
      keys: Object.keys(record),
    });
    return {
      ...normalized,
      ok: false,
      kind: "error",
      status: 502,
      errorCode: "INVALID_RESPONSE",
      errorMessage: "Image generation returned no asset",
      imageUrl: undefined,
      imageBase64: undefined,
    };
  }

  return normalized;
}
